import React, { useEffect, useMemo, useState, useCallback } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { RefreshCw, Download, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
// Iter-312 · BI Isolation — Bali date helpers, presets and the brand
// range resolver live in ReportingEngine.jsx. Snapshot helpers live
// in NotificationEngine.jsx.
import { todayBali } from "@/lib/ReportingEngine";
import { useDashboardSnapshot, formatSnapshotDateLabel } from "@/lib/NotificationEngine";

// Period defaults mirror the Cancellation / Service Performance /
// Staff Utilisation dashboards: A = the previous full calendar month,
// B = the current month up to today (Bali).
function previousMonthRange(todayISO) {
  const [y, m] = todayISO.split("-").map(Number);
  const prevYear  = m === 1 ? y - 1 : y;
  const prevMonth = m === 1 ? 12 : m - 1;
  const lastDay   = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  return [
    `${prevYear}-${String(prevMonth).padStart(2, "0")}-01`,
    `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
  ];
}
function currentMonthToToday(todayISO) {
  const [y, m] = todayISO.split("-");
  return [`${y}-${m}-01`, todayISO];
}

// Same contract as the backend's `_pct_delta`: how Period B moved
// relative to the Period A baseline. Null when A is zero (no divisor).
function pctDelta(a, b) {
  const av = Number(a || 0);
  const bv = Number(b || 0);
  if (!av) return null;
  return Math.round(((bv - av) / av) * 1000) / 10;
}

function DeltaBadge({ delta, testId }) {
  let Icon = Minus;
  let cls = "text-muted-foreground";
  if (delta != null && delta > 0) { Icon = ArrowUpRight; cls = "text-emerald-600"; }
  if (delta != null && delta < 0) { Icon = ArrowDownRight; cls = "text-rose-600"; }
  const label = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-from`} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input type="date" value={to} onChange={(e) => onTo(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-to`} />
    </div>
  );
}

export default function BrandDashboard() {
  const { staff } = useAuth();
  const [summary, setSummary] = useState(null);
  const [summaryB, setSummaryB] = useState(null);
  const [branches, setBranches] = useState([]);
  const [conciergeUsage, setConciergeUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const today = todayBali();
  const [aFrom, setAFrom] = useState(() => previousMonthRange(today)[0]);
  const [aTo,   setATo]   = useState(() => previousMonthRange(today)[1]);
  const [bFrom, setBFrom] = useState(() => currentMonthToToday(today)[0]);
  const [bTo,   setBTo]   = useState(() => currentMonthToToday(today)[1]);
  const nav = useNavigate();

  // Iter-412 — Brand Overview access is gated SOLELY by the per-user
  // `brand_overview` tick on Brand Report Permissions. Owner always
  // bypasses; Admin no longer gets automatic access.
  const [brandOverviewTick, setBrandOverviewTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setBrandOverviewTick(keys.has("brand_overview"));
      })
      .catch(() => { /* non-blocking — backend returns 403 if denied */ });
    return () => { cancelled = true; };
  }, []);
  const canViewReports = isOwner(staff) || brandOverviewTick;

  const range = useMemo(() => ({ date_from: aFrom, date_to: aTo }), [aFrom, aTo]);
  const rangeB = useMemo(() => ({ date_from: bFrom, date_to: bTo }), [bFrom, bTo]);

  // The branch list never changes with a date filter — fetch once so
  // the per-branch currency lookup stays fresh even while the summary
  // is loading a new range.
  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranches(r.data)).catch((err) => toast.error(formatApiError(err)));
    staffApi.get("/concierge/usage").then((r) => setConciergeUsage(r.data)).catch(() => { /* silent */ });
  }, []);

  const load = useCallback(() => {
    if (!canViewReports) return;
    setLoading(true);
    Promise.all([
      staffApi.get("/reports/brand/summary", { params: range }),
      staffApi.get("/reports/brand/summary", { params: rangeB }),
    ])
      .then(([ra, rb]) => { setSummary(ra.data); setSummaryB(rb.data); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canViewReports, range, rangeB]);

  useEffect(() => { load(); }, [load]);

  const branchById = Object.fromEntries(branches.map((b) => [b.id, b]));
  // Period B rows keyed by branch so each location card can pair its
  // own baseline without a second lookup pass.
  const bRowById = useMemo(
    () => Object.fromEntries((summaryB?.branches || []).map((b) => [b.branch_id, b])),
    [summaryB],
  );

  const dateLabel = `${formatSnapshotDateLabel(range.date_from, range.date_to)} (A) vs ${formatSnapshotDateLabel(rangeB.date_from, rangeB.date_to).replace(/^Report for /, "")} (B)`;
  const { downloadRef, downloadPng, capturing } = useDashboardSnapshot({
    label: "Brand Overview",
    dateFrom: range.date_from,
    dateTo: range.date_to,
  });

  return (
    <StaffShell context="brand">
      <div ref={downloadRef} data-testid="brand-overview-snapshot-root">
      <div
        className="snapshot-only text-center px-4 py-3 border border-border rounded-lg bg-muted/30 mb-4"
        data-testid="brand-overview-snapshot-header"
      >
        <p className="tracking-overline text-[10px] text-muted-foreground">COCOON MEDICAL SPA</p>
        <p className="text-xl font-semibold mt-1">Brand Overview</p>
        {dateLabel && (
          <p className="text-sm text-muted-foreground mt-1" data-testid="brand-overview-snapshot-date">
            {dateLabel}
          </p>
        )}
      </div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Brand overview</h1>
          {dateLabel && (
            <p
              className="text-xs font-medium text-sky-600 mt-1"
              data-testid="brand-overview-date-label"
            >
              {dateLabel}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={downloadPng}
          disabled={capturing || loading}
          className="gap-1 h-9"
          data-testid="brand-overview-download-png"
          data-html2canvas-ignore="true"
        >
          <Download className={`w-4 h-4 ${capturing ? "animate-pulse" : ""}`} />
          {capturing ? "Capturing…" : "Download Dashboard (PNG)"}
        </Button>
      </div>

      {canViewReports && (
        <>
          <Card className="rounded-lg mb-4" data-testid="brand-date-range-card">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-overview-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-overview-b" />
              <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1 ml-auto" data-testid="brand-overview-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-lg mb-6" data-testid="brand-total-card">
            <CardContent className="p-6">
              <p className="tracking-overline text-[10px] text-muted-foreground">TOTAL REVENUE (RECORDED)</p>
              <p className="text-4xl font-semibold mt-2" data-testid="brand-total-value">
                {loading ? "…" : summary ? formatIDR(summary.total_revenue) : "—"}
              </p>
              <div className="mt-2 flex items-center gap-3 text-[11px]">
                <DeltaBadge
                  delta={pctDelta(summary?.total_revenue, summaryB?.total_revenue)}
                  testId="brand-total-delta"
                />
                <span className="text-muted-foreground" data-testid="brand-total-value-b">
                  B · {summaryB ? formatIDR(summaryB.total_revenue) : "—"}
                </span>
              </div>
              {summary?.date_from && summary?.date_to && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  A · {summary.date_from} → {summary.date_to}
                  {summaryB?.date_from && summaryB?.date_to
                    ? `  ·  B · ${summaryB.date_from} → ${summaryB.date_to}`
                    : ""}
                </p>
              )}
            </CardContent>
          </Card>

          <p className="tracking-overline text-[10px] text-muted-foreground mb-3">BY LOCATION</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(summary?.branches || []).map((b) => {
              const bb = bRowById[b.branch_id] || {};
              const slug = b.branch.toLowerCase();
              return (
              <Card
                key={b.branch_id}
                onClick={() => nav(`/staff/branch/${b.branch_id}/overview`)}
                className="rounded-lg cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-primary/30"
                data-testid={`brand-branch-${slug}`}
              >
                <CardContent className="p-5">
                  <p className="font-medium">{b.branch}</p>
                  <p className="text-2xl font-semibold mt-2" data-testid={`brand-branch-${slug}-revenue`}>
                    {formatBranchMoney(branchById[b.branch_id], b.revenue)}
                  </p>
                  <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                    <DeltaBadge delta={pctDelta(b.revenue, bb.revenue)} testId={`brand-branch-${slug}-revenue-delta`} />
                    <span className="text-muted-foreground" data-testid={`brand-branch-${slug}-revenue-b`}>
                      B · {formatBranchMoney(branchById[b.branch_id], bb.revenue || 0)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-muted-foreground">
                    <div>
                      Orders
                      <div className="text-foreground text-base" data-testid={`brand-branch-${slug}-orders`}>{b.orders}</div>
                      <div className="flex items-center gap-1.5">
                        <DeltaBadge delta={pctDelta(b.orders, bb.orders)} testId={`brand-branch-${slug}-orders-delta`} />
                        <span data-testid={`brand-branch-${slug}-orders-b`}>B · {bb.orders ?? 0}</span>
                      </div>
                    </div>
                    <div>
                      Appts
                      <div className="text-foreground text-base" data-testid={`brand-branch-${slug}-appts`}>{b.appointments}</div>
                      <div className="flex items-center gap-1.5">
                        <DeltaBadge delta={pctDelta(b.appointments, bb.appointments)} testId={`brand-branch-${slug}-appts-delta`} />
                        <span data-testid={`brand-branch-${slug}-appts-b`}>B · {bb.appointments ?? 0}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-primary/80 mt-4">Open branch overview →</p>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </>
      )}

      {!canViewReports && (
        <Card className="rounded-lg mb-6" data-testid="brand-no-reports-card">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            Welcome, {staff?.full_name}. Use the sidebar to reach the tools your role can access. Financial reports are restricted for this role.
          </CardContent>
        </Card>
      )}

      {conciergeUsage && (
        <>
          <p className="tracking-overline text-[10px] text-muted-foreground mt-8 mb-3">AI CONCIERGE USAGE</p>
          <Card className="rounded-lg" data-testid="brand-concierge-usage-card">
            <CardContent className="p-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="tracking-overline text-[10px] text-muted-foreground">TODAY</p>
                  <p className="text-2xl font-semibold mt-1" data-testid="concierge-usage-today">{conciergeUsage.today}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{conciergeUsage.active_customers_today} active {conciergeUsage.active_customers_today === 1 ? "guest" : "guests"}</p>
                </div>
                <div>
                  <p className="tracking-overline text-[10px] text-muted-foreground">LAST 7 DAYS</p>
                  <p className="text-2xl font-semibold mt-1" data-testid="concierge-usage-week">{conciergeUsage.week}</p>
                </div>
                <div>
                  <p className="tracking-overline text-[10px] text-muted-foreground">LAST 30 DAYS</p>
                  <p className="text-2xl font-semibold mt-1" data-testid="concierge-usage-month">{conciergeUsage.month}</p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-4">
                Guest messages sent to Claude. Cap is {conciergeUsage.daily_cap_per_customer} messages per guest per day.
              </p>
            </CardContent>
          </Card>
        </>
      )}
      </div>
    </StaffShell>
  );
}
